/**
 * @file The staging list: every part the operator has entered, in the order they entered it.
 * Consumer: `SignifyFetchJobEntry`. It subscribes to `keys` alone and reads nothing out of `byKey`,
 * so it re-renders on an add, a remove or a clear and never on a quantity or text edit — at a
 * thousand rows that is the difference between one cell repainting and all of them.
 * Each column `render` hands its row's proxy to a cell that subscribes for itself. A row's source
 * never changes once added, which is why the Source cell reads it off the proxy directly.
 * `virtual` requires numeric `scroll.x` and `scroll.y` and a fixed `width` on every column.
 */

import { FC, useMemo } from 'react';
import { Button, Table, Tag, Tooltip, Typography, type TableProps } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import FlexCol from '@/components/atoms/FlexCol';
import SignifyFetchJobInputPartCell from './SignifyFetchJobInputPartCell';
import SignifyFetchJobInputQuantityCell from './SignifyFetchJobInputQuantityCell';
import type { SignifyFetchJobDraft } from '../index.types';

const { Text } = Typography;

const TABLE_WIDTH = 840;
const TABLE_HEIGHT = 360;
const NO_QUANTITY_COLUMN_REASON = 'This table has no Quantity column; quantities are not inserted';

type SignifyFetchJobInputRow = { key: string; index: number };

type SignifyFetchJobInputTableProps = {
  draft: SignifyFetchJobDraft;
  hasQuantityColumn: boolean;
  onCommitInput: (key: string, previousInput: string) => void;
  onRemove: (key: string) => void;
};

const SignifyFetchJobInputTable: FC<SignifyFetchJobInputTableProps> = ({
  draft,
  hasQuantityColumn,
  onCommitInput,
  onRemove,
}) => {
  const { keys } = useSnapshot(draft);
  const dataSource = useMemo<SignifyFetchJobInputRow[]>(
    () => keys.map((key, index) => ({ key, index })),
    [keys],
  );

  const columns = useMemo<TableProps<SignifyFetchJobInputRow>['columns']>(() => [
    {
      title: '#',
      key: 'position',
      width: 50,
      render: (_value, record) => <Text type="secondary">{record.index + 1}</Text>,
    },
    {
      title: 'Part',
      key: 'part',
      width: 420,
      render: (_value, record) => {
        const row = draft.byKey[record.key];
        return row ? <SignifyFetchJobInputPartCell row={row} onCommitInput={onCommitInput} /> : null;
      },
    },
    {
      title: 'Source',
      key: 'source',
      width: 200,
      render: (_value, record) =>
        draft.byKey[record.key]?.product ? (
          <Tag color="blue">Search</Tag>
        ) : (
          <FlexCol gap={0}>
            <Tag>Pasted</Tag>
            <Text type="secondary">Resolves when the fetch starts</Text>
          </FlexCol>
        ),
    },
    {
      title: (
        <Tooltip title={hasQuantityColumn ? undefined : NO_QUANTITY_COLUMN_REASON}>
          <Text>Qty</Text>
        </Tooltip>
      ),
      key: 'quantity',
      width: 110,
      render: (_value, record) => {
        const row = draft.byKey[record.key];
        return row ? (
          <SignifyFetchJobInputQuantityCell row={row} disabledReason={hasQuantityColumn ? null : NO_QUANTITY_COLUMN_REASON} />
        ) : null;
      },
    },
    {
      title: '',
      key: 'remove',
      width: 60,
      render: (_value, record) => (
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          aria-label={`signify-fetch-job-remove-${record.key}`}
          onClick={() => onRemove(record.key)}
        />
      ),
    },
  ], [draft, hasQuantityColumn, onCommitInput, onRemove]);

  return (
    <Table<SignifyFetchJobInputRow>
      virtual
      aria-label="signify-fetch-job-input-table"
      size="small"
      rowKey="key"
      columns={columns}
      dataSource={dataSource}
      pagination={false}
      scroll={{ x: TABLE_WIDTH, y: TABLE_HEIGHT }}
      locale={{ emptyText: <Text type="secondary">Paste codes or search to add parts</Text> }}
    />
  );
};

export default SignifyFetchJobInputTable;
